import { ZuiFormAssociatedElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { style } from './zui-textarea-css.js';

/**
 * @element zui-textarea
 *
 */

export class ZuiTextareaElement extends ZuiFormAssociatedElement {
  protected get _focusControlSelector() {
    return 'textarea';
  }

  /**
   * Input value that is associated with form submission
   */
  @property({ type: String })
  get value(): string {
    return this.#value;
  }

  set value(val: string) {
    const oldVal = this.#value;
    this.#value = val;
    this._setFormValue(this.#value);
    this.requestUpdate('value', oldVal);
  }

  /**
   * An optional placeholder value (e.g. "ghost text"). Use this to provide extra contextual information, but do not use in place of a label!
   */
  @property({ type: String })
  placeholder = '';

  /**
   * Represents that this control must be filled in for form submission
   */
  @property({ type: Boolean })
  required = false;

  /**
   * Represents the maximum length of characters supported by value. Only applicable to text-based inputs (e.g. `text`, `search`)
   */
  @property({ type: Number, attribute: 'maxlength' })
  maxLength = 524288;

  /**
   * Represents the minimum length of characters supported by value. Only applicable to text-based inputs (e.g. `text`, `search`)
   */
  @property({ type: Number, attribute: 'minlength' })
  minLength: number | null = null;

  #value = '';

  static get styles() {
    return [super.styles, style];
  }

  connectedCallback() {
    super.connectedCallback();

    const value = this.value ?? this.getAttribute('value');
    this._setFormValue(value);
  }

  protected formResetCallback() {
    this.value = '';
  }

  render() {
    return html`<textarea
      .value="${this.value}"
      @input="${(e: InputEvent) => this.#onInput(e)}"
      @change="${(e: Event) => this.#onChange(e)}"
      placeholder="${this.placeholder ?? ''}"
      ?disabled="${this.disabled}"
      ?readonly="${this.readOnly}"
      maxlength="${ifDefined(this.maxLength)}"
      minlength="${ifDefined(this.minLength)}"
    ></textarea> `;
  }

  #onInput(e: InputEvent) {
    this.value = (e.target as HTMLTextAreaElement).value;
  }

  #onChange(e: Event) {
    this.value = (e.target as HTMLTextAreaElement).value;
    this.dispatchEvent(new CustomEvent('change', { detail: this.value, bubbles: true }));
  }
}

window.customElements.define('zui-textarea', ZuiTextareaElement);

declare global {
  interface HTMLElementTagNameMap {
    'zui-textarea': ZuiTextareaElement;
  }
}
